import React, { useState, useEffect } from "react";
import { Box, Grid, Paper, Typography, Alert, LinearProgress } from "@mui/material";
import PaymentProfileTable from "./PaymentProfileTable";
import contractItemsApi from "../../services/contractItems.service";
import usePaymentItemScopes from "./usePaymentItemScopes";
import { AppButton } from "components/common/buttons";

const PaymentManagement = ({ contractId, contractData }) => {
  const [contractItems, setContractItems] = useState([]);
  const [loadedContractId, setLoadedContractId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [itemLoadKey, setItemLoadKey] = useState(0);
  const itemScopes = usePaymentItemScopes({ open: !!contractId, contractId, items: contractItems, includeAll: true });

  useEffect(() => {
    let active = true;
    setContractItems([]);
    setLoadError("");
    setLoading(true);
    if (contractId) contractItemsApi.getItemsHierarchicalByContractId(contractId).then((result) => {
      if (result.success === false || result.Success === false) throw new Error("Không thể tải hạng mục.");
      if (active) setContractItems(result.data || []);
    }).catch(() => {
      if (active) setLoadError("Không thể tải hạng mục hợp đồng. Hãy tải lại trước khi thêm thanh toán.");
    }).finally(() => {
      if (active) { setLoading(false); setLoadedContractId(contractId); }
    });
    return () => { active = false; };
  }, [contractId, itemLoadKey]);

  if (!contractId || !contractData) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Alert severity="info">
          Vui lòng chọn một hợp đồng để quản lý thanh toán
        </Alert>
      </Box>
    );
  }

  if (loadError) {
    return <Box sx={{ p: 3 }}><Alert severity="error" action={<AppButton onClick={() => setItemLoadKey((value) => value + 1)}>Thử lại</AppButton>}>{loadError}</Alert></Box>;
  }

  if (loading || loadedContractId !== contractId) return <LinearProgress aria-label="Đang tải hạng mục thanh toán" />;

  if (contractItems.length === 0 && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}
        >
          Quản lý thanh toán hợp đồng
        </Typography>
        <Alert severity="warning">
          Hợp đồng này chưa có hạng mục. Vui lòng thêm hạng mục trước khi tạo kỳ
          thanh toán.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          fontFamily: "Inter, sans-serif",
          fontWeight: 600,
          color: "#333",
          mb: 1,
        }}
      >
        Quản lý thanh toán hợp đồng
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        gutterBottom
        sx={{ mb: 3 }}
      >
        {contractData.contractNumber} - {contractData.contractName}
      </Typography>

      {itemScopes.loading && <LinearProgress aria-label="Đang tải phân cấp thanh toán" />}
      {itemScopes.error && <Alert severity="error" action={<AppButton onClick={itemScopes.retry}>Thử lại</AppButton>}>{itemScopes.error}</Alert>}
      {!itemScopes.loading && !itemScopes.error && <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Paper
            sx={{
              p: 3,
              border: "1px solid #e0e0e0",
              boxShadow: "none",
              borderRadius: 2,
            }}
          >
            <PaymentProfileTable
              contractId={contractId}
              contractNumber={contractData.contractNumber}
              contractName={contractData.contractName}
              contractItems={contractItems}
              sections={itemScopes.sections}
              contractData={contractData}
            />
          </Paper>
        </Grid>
      </Grid>}

      <Box sx={{ mt: 3 }}>
          <Alert severity="info" sx={{ fontFamily: "Inter, sans-serif" }}>
            <Typography variant="body2">
              <strong>Hướng dẫn sử dụng:</strong>
            </Typography>
            <Typography variant="body2" component="div" sx={{ mt: 1 }}>
              1. Tạo các kỳ thanh toán cho hợp đồng (VD: Tạm ứng, Thanh toán lần
              1, Nghiệm thu...)
              <br />
              2. Bấm nút mở kỳ để xem các bảng theo loại hạng mục và tỉnh/xã.
              <br />
              3. Chọn <strong>Thêm chi tiết</strong>, chọn loại hạng mục, tỉnh/xã rồi chọn hạng mục cần thanh toán.
              <br />
              4. {"VAT áp dụng riêng cho từng kỳ. Scope Tổng hợp không được chọn để thanh toán."}
            </Typography>
          </Alert>
      </Box>
    </Box>
  );
};

export default PaymentManagement;
